import React, { useEffect, useState, useMemo, useRef } from "react";
import _ from "lodash";
import classes from "./VirtualisedFiltersPopup.scss";
import {
  Dropdown,
  SideNavigation,
  Button,
  Checkbox,
  Badge,
  SearchInput,
  Tooltip,
  RadioButton,
  ButtonDisclosure,
} from "@toddle-edu/ds-web";
import { FilterOutlined, ChevronRightOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import classNames from "classnames";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { Virtuoso } from "react-virtuoso";
import {
  SCHOOL_DEPENDENT_FILTERS,
  getSelectedFilterCount,
  getFilteredOptionsBySchool,
  buildFlatItemsList,
} from "./VirtualisedFiltersPopupUtils";
import {
  FILTER_NAME_CLASSES,
  FILTER_NAME_COURSES,
  FILTER_NAME_CREATORS,
} from "../../../Courses/routes/Course/Constants/StringConstants";

const VirtualisedFiltersPopup = ({
  disabled = false,
  options,
  defaultSelectedOptions,
  onFilterChange,
  isFetchingMoreDataForCourseFilter,
  isCourseListLoading,
  hasNextPageForCourseFilter,
  loadMoreCourseFilterFeed,
  isFetchingMoreDataForClasses,
  isClassesListLoading,
  hasNextPageForClasses,
  loadMoreClassFeed,
  isFetchingMoreDataForCreatorFilter,
  isCreatorListLoading,
  hasNextPageForCreatorFilter,
  loadMoreCreatorFilterFeed,
}) => {
  const optionKeys = _.keys(options);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(optionKeys[0]);
  const prevDefaultSelectedOptions = useRef(null);

  const [selectedOptions, setSelectedOptions] = useState(
    defaultSelectedOptions
  );
  const [searchText, setSearchText] = useState("");

  const { t } = useI18n();

  useEffect(() => {
    if (
      !_.isEqual(prevDefaultSelectedOptions.current, defaultSelectedOptions)
    ) {
      prevDefaultSelectedOptions.current = defaultSelectedOptions;
      resetState();
    }
  }, [defaultSelectedOptions]);

  useEffect(() => {
    setSearchText("");
  }, [selectedFilter]);

  const updateSelectedOptions = ({ key, value }) => {
    setSelectedOptions(prevState => ({ ...prevState, [key]: value }));
  };

  const resetState = () => {
    setSelectedOptions(defaultSelectedOptions);
    setSelectedFilter(optionKeys[0]);
    setSearchText("");
  };

  const selectedSchools = _.get(selectedOptions, "curriculumProgramIds", []);
  const allSchoolOptions = _.get(options, "curriculumProgramIds.options", []);

  const filteredOptions = useMemo(
    () =>
      getFilteredOptionsBySchool({
        options,
        selectedSchools,
      }),
    [options, selectedSchools]
  );

  const showSectionHeaders =
    _.size(allSchoolOptions) > 1 && _.size(selectedSchools) !== 1;

  const selectedFiltersCount = getSelectedFilterCount({
    optionKeys,
    selectedOptions,
  });

  const getCountComponent = (count, key) => {
    return (
      <div
        className={classes.suffixContainer}
        onClick={() => onNavigationClick(key)}
      >
        <Badge type="numeric" variant="subtle" value={count} />
        <ChevronRightOutlined variant="subtle" />
      </div>
    );
  };

  const navigationOptions = useMemo(() => {
    const navigationTemp = [
      {
        key: "root",
        children: optionKeys,
      },
    ];
    _.forEach(optionKeys, option => {
      const suffixCount = _.isArray(selectedOptions[option])
        ? _.size(selectedOptions[option])
        : 0;

      navigationTemp.push({
        key: option,
        tier: 1,
        parent: "root",
        title: options[option].title,
        suffix: getCountComponent(suffixCount, option),
        children: [],
      });
    });
    return navigationTemp;
  }, [optionKeys, selectedOptions, options]);

  const onNavigationClick = key => {
    setSelectedFilter(key);
  };

  const getResetDependentFilters = ({ curriculumProgramIds }) => {
    const resetDependents = {};
    _.forEach(optionKeys, key => {
      if (_.includes(SCHOOL_DEPENDENT_FILTERS, key)) {
        resetDependents[key] = [];
      }
    });
    return { ...resetDependents, curriculumProgramIds };
  };

  const onCheckBoxChange = ({ isChecked, value }) => {
    if (selectedFilter === "curriculumProgramIds") {
      const newSchoolValues = isChecked
        ? _.without(selectedOptions.curriculumProgramIds, value)
        : [...(selectedOptions.curriculumProgramIds || []), value];

      setSelectedOptions(prev => ({
        ...prev,
        ...getResetDependentFilters({ curriculumProgramIds: newSchoolValues }),
      }));
    } else if (isChecked) {
      updateSelectedOptions({
        key: selectedFilter,
        value: _.without(selectedOptions[selectedFilter], value),
      });
    } else {
      updateSelectedOptions({
        key: selectedFilter,
        value: [...(selectedOptions[selectedFilter] || []), value],
      });
    }
  };

  const onAllCheckBoxClick = ({ isChecked }) => {
    if (selectedFilter === "curriculumProgramIds") {
      const schools = isChecked ? [] : _.map(allSchoolOptions, "value");
      setSelectedOptions(prev => ({
        ...prev,
        ...getResetDependentFilters({ curriculumProgramIds: schools }),
      }));
    } else if (isChecked) {
      updateSelectedOptions({ key: selectedFilter, value: [] });
    } else {
      const currentFilterOptions = _.get(
        filteredOptions,
        `${selectedFilter}.options`,
        []
      );
      const selectableOptions = _.filter(
        currentFilterOptions,
        option => !option.isSectionHeader
      );

      updateSelectedOptions({
        key: selectedFilter,
        value: _.map(selectableOptions, "value"),
      });
    }
  };

  const onSectionHeaderCheckBoxClick = ({ isChecked, curriculumProgramId }) => {
    const currentFilterOptions = _.get(
      filteredOptions,
      `${selectedFilter}.options`,
      []
    );
    const optionsForSection = _.filter(
      currentFilterOptions,
      option =>
        option.curriculumProgramId === curriculumProgramId &&
        !option.isSectionHeader
    );

    const optionValuesForSection = _.map(optionsForSection, "value");

    if (isChecked) {
      updateSelectedOptions({
        key: selectedFilter,
        value: _.difference(
          selectedOptions[selectedFilter],
          optionValuesForSection
        ),
      });
    } else {
      updateSelectedOptions({
        key: selectedFilter,
        value: _.uniq([
          ...(selectedOptions[selectedFilter] || []),
          ...optionValuesForSection,
        ]),
      });
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(prevValue => {
      if (prevValue) {
        resetState();
      }
      return !prevValue;
    });
  };

  const onSubmit = () => {
    // Batch all changed filters into a single call for clarity
    const changedFilters = {};
    _.forEach(optionKeys, key => {
      if (!_.isEqual(selectedOptions[key], defaultSelectedOptions[key])) {
        changedFilters[key] = selectedOptions[key];
      }
    });
    if (!_.isEmpty(changedFilters)) {
      onFilterChange(changedFilters);
    }

    toggleDropdown();
  };

  const onScrollEnd = () => {
    if (selectedFilter === FILTER_NAME_COURSES) {
      if (
        hasNextPageForCourseFilter &&
        !isFetchingMoreDataForCourseFilter &&
        !isCourseListLoading
      ) {
        loadMoreCourseFilterFeed();
      }
    } else if (selectedFilter === FILTER_NAME_CLASSES) {
      if (
        hasNextPageForClasses &&
        !isFetchingMoreDataForClasses &&
        !isClassesListLoading
      ) {
        loadMoreClassFeed();
      }
    } else if (selectedFilter === FILTER_NAME_CREATORS) {
      if (
        hasNextPageForCreatorFilter &&
        !isFetchingMoreDataForCreatorFilter &&
        !isCreatorListLoading
      ) {
        loadMoreCreatorFilterFeed();
      }
    }
  };

  const flatItemsList = useMemo(
    () =>
      buildFlatItemsList({
        filteredOptions,
        selectedFilter,
        searchText,
        showSectionHeaders,
      }),
    [filteredOptions, selectedFilter, searchText, showSectionHeaders]
  );

  useEffect(() => {
    if (!_.includes(optionKeys, selectedFilter)) {
      setSelectedFilter(optionKeys[0]);
    }
  }, [optionKeys, selectedFilter]);

  const listOptions = _.get(filteredOptions, `${selectedFilter}.options`, []);
  const allText =
    _.get(options, `${selectedFilter}.allText`) || t("common:all");
  const uiType = _.get(options, `${selectedFilter}.uiType`, "checkbox");

  const nonSectionHeaderOptions = _.filter(
    listOptions,
    option => !option.isSectionHeader
  );

  // Search threshold: use unfiltered options so search bar doesn't disappear
  // when school filter narrows the list below 10 items
  const unfilteredNonHeaders = _.filter(
    _.get(options, `${selectedFilter}.options`, []),
    option => !option.isSectionHeader
  );
  const showSearch = _.size(unfilteredNonHeaders) > 10;

  const allSelected =
    _.isEqual(
      _.size(nonSectionHeaderOptions),
      _.size(selectedOptions[selectedFilter])
    ) && !_.isEmpty(nonSectionHeaderOptions);

  const hasCheckboxItems = _.some(
    flatItemsList,
    item => item.type === "checkbox"
  );

  const renderVirtuosoItem = (index, item) => {
    if (item.type === "all") {
      return (
        <Checkbox
          isChecked={allSelected}
          onChange={() =>
            onAllCheckBoxClick({
              isChecked: allSelected,
            })
          }
          isIndeterminate={
            !allSelected && !_.isEmpty(selectedOptions[selectedFilter])
          }
          labelClassName="text-body"
          checkboxWrapperClassName={classes.checkBoxContainer}
          testId={`learningCoursesList-FilterHeader-${selectedFilter}`}
          dsVersion="2.0"
        >
          {allText}
        </Checkbox>
      );
    }

    if (item.type === "separator") {
      return <div className={classes.separator} />;
    }

    if (item.type === "sectionHeader") {
      const { sectionValues } = item;

      const selectedCount = _.size(
        _.intersection(selectedOptions[selectedFilter], sectionValues)
      );
      const allSectionOptionsSelected = selectedCount === _.size(sectionValues);
      const someSectionOptionsSelected =
        selectedCount > 0 && !allSectionOptionsSelected;

      return (
        <Checkbox
          isChecked={allSectionOptionsSelected}
          isIndeterminate={someSectionOptionsSelected}
          onChange={() =>
            onSectionHeaderCheckBoxClick({
              isChecked: allSectionOptionsSelected,
              curriculumProgramId: item.curriculumProgramId,
            })
          }
          checkboxWrapperClassName={classes.checkBoxContainer}
          testId={`learningCoursesList-FilterHeader-section-${index}`}
          dsVersion="2.0"
        >
          <span
            className={classNames(classes.checkBoxSectionLabel, "text-label")}
          >
            {item.label}
          </span>
        </Checkbox>
      );
    }

    // type === "checkbox"
    const isChecked = _.includes(selectedOptions[selectedFilter], item.value);

    return (
      <Checkbox
        onChange={() =>
          onCheckBoxChange({
            isChecked,
            value: item.value,
          })
        }
        isChecked={isChecked}
        checkboxWrapperClassName={classNames(classes.checkBoxContainer, {
          [classes.isArchived]: item.isArchived,
        })}
        dsVersion="2.0"
        testId={`learningCoursesList-FilterHeader-${selectedFilter}-${index}`}
      >
        <div className={classes.thumbnailContainer}>
          <div className={classes.thumbnailContainerMenuItem}>
            <ConditionalWrapper
              type="wrapperAsElement"
              wrapper={<Tooltip tooltip={item.label} placement="top" />}
              condition={_.size(item.label) > 28}
            >
              <span className={classNames(classes.checkBoxLabel, "text-body")}>
                {item.label}
              </span>
            </ConditionalWrapper>
            {item.subLabel && (
              <ConditionalWrapper
                type="wrapperAsElement"
                wrapper={<Tooltip tooltip={item.subLabel} placement="top" />}
                condition={_.size(item.subLabel) > 28}
              >
                <span>{item.subLabel}</span>
              </ConditionalWrapper>
            )}
          </div>
        </div>
      </Checkbox>
    );
  };

  const renderSearchBar = () => {
    if (!showSearch) return null;
    return (
      <div
        className={classes.searchContainer}
        onKeyUp={e => e.preventDefault()}
      >
        <SearchInput
          value={searchText}
          placeholder={t("common:search")}
          onChange={value => setSearchText(value)}
          testId="learningCoursesList-FilterHeader-filter"
          isDropdownSearch={true}
        />
      </div>
    );
  };

  const getRightPaneContent = () => {
    if (uiType === "RADIO") {
      return (
        <div className={classes.sectionContainer}>
          {_.map(listOptions, option => (
            <div className={classes.radioItem} key={`section-${option.key}`}>
              <RadioButton
                value={option.value}
                size="medium"
                checked={_.includes(
                  selectedOptions[selectedFilter],
                  option.value
                )}
                onChange={() =>
                  updateSelectedOptions({
                    key: selectedFilter,
                    value: option.value,
                  })
                }
                testId={`learningCoursesList-FilterHeader-${selectedFilter}-${option.key}`}
              >
                <span className={"text-body"}>{option.label}</span>
              </RadioButton>
            </div>
          ))}
        </div>
      );
    }

    if (_.isEmpty(nonSectionHeaderOptions)) {
      return (
        <div className={classes.emptyState}>
          <span className="text-body">
            {t("common:no_with_label", {
              label: t("common:option_plural"),
            })}
          </span>
        </div>
      );
    }

    return (
      <>
        {renderSearchBar()}
        {!hasCheckboxItems && !_.isEmpty(searchText) ? (
          <div className={classes.emptyState}>
            <span className="text-body">{t("common:noResultHeading")}</span>
          </div>
        ) : (
          <Virtuoso
            data={flatItemsList}
            className={classes.sectionContainer}
            computeItemKey={(_index, item) => item.key}
            itemContent={renderVirtuosoItem}
            increaseViewportBy={400}
            endReached={onScrollEnd}
          />
        )}
      </>
    );
  };

  return (
    <Dropdown
      placement="bottomRight"
      visible={showDropdown}
      disabled={disabled}
      onVisibleChange={toggleDropdown}
      overlay={
        <div className={classes.filtersContainer}>
          <div className={classes.innerContainer}>
            <div className={classes.leftPaneContainer}>
              <SideNavigation
                onClick={onNavigationClick}
                options={navigationOptions}
                selectedOptionKey={selectedFilter}
                testId="learningCoursesList-FilterHeader"
                theme="light"
                dsVersion="2.0"
              />
            </div>
            <div className={classes.rightPaneContainer}>
              {getRightPaneContent()}
            </div>
          </div>
          <div className={classes.footer}>
            {selectedFiltersCount > 0 ? (
              <div className="text-label">
                {t("classRoom:count_filter_applied", {
                  count: selectedFiltersCount,
                })}
              </div>
            ) : null}
            <div className={classes.rightFooter}>
              <Button
                variant="outlined-subtle"
                type="outlined"
                onClick={toggleDropdown}
                testId="learningCoursesList-FilterHeader-cancel"
              >
                {t("common:cancel")}
              </Button>
              <Button
                variant="progressive"
                type="fill"
                onClick={onSubmit}
                testId="learningCoursesList-FilterHeader-apply"
              >
                {t("common:apply")}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <ButtonDisclosure
        type="outlined"
        variant="neutral"
        size="medium"
        dsVersion="2.0"
        icon={<FilterOutlined />}
        isActivated={false}
        badgeProps={{
          value: selectedFiltersCount,
          variant: "dark",
          shape: "circle",
        }}
        testId="learningCoursesList-FilterHeader-filter-icon"
      >
        {t("common:filter")}
      </ButtonDisclosure>
    </Dropdown>
  );
};

export default VirtualisedFiltersPopup;
